// Global active-organization context. Source of truth for v1 multi-tenant UI.
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { useAuth } from "./auth";

const OrgContext = createContext(null);
const ACTIVE_ORG_KEY = "aivos_active_org";

export const OrgProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [activeOrgId, setActiveOrgIdState] = useState(() => localStorage.getItem(ACTIVE_ORG_KEY) || null);
  const [memberships, setMemberships] = useState([]);
  const [activeRole, setActiveRole] = useState(null);
  const [loading, setLoading] = useState(false);

  const setActiveOrgId = useCallback((id) => {
    if (id) {
      localStorage.setItem(ACTIVE_ORG_KEY, id);
    } else {
      localStorage.removeItem(ACTIVE_ORG_KEY);
    }
    setActiveOrgIdState(id);
  }, []);

  const refresh = useCallback(async () => {
    if (!user) {
      setMemberships([]);
      setActiveRole(null);
      return;
    }
    setLoading(true);
    try {
      const headers = activeOrgId ? { "X-Organization-Id": activeOrgId } : {};
      const r = await api.get("/v1/me/context", { headers });
      const data = r.data;
      setMemberships(data.memberships || []);
      const validIds = (data.memberships || []).map((m) => m.organization.id);
      if (activeOrgId && validIds.includes(activeOrgId)) {
        setActiveRole(data.active_role || null);
      } else if (data.active_organization_id) {
        setActiveOrgId(data.active_organization_id);
        setActiveRole(data.active_role || null);
      } else {
        setActiveOrgId(null);
        setActiveRole(null);
      }
    } catch (err) {
      if (err?.response?.status !== 401) {
        console.error("OrgContext refresh failed:", err);
      }
      setMemberships([]);
      setActiveRole(null);
    } finally {
      setLoading(false);
    }
  }, [user, activeOrgId, setActiveOrgId]);

  useEffect(() => {
    if (!authLoading) refresh();
  }, [authLoading, user?.id, user?._id, refresh]);

  const activeOrg = memberships.find((m) => m.organization.id === activeOrgId)?.organization || null;

  return (
    <OrgContext.Provider
      value={{
        memberships,
        activeOrgId,
        activeOrg,
        activeRole,
        setActiveOrgId,
        refresh,
        loading,
      }}
    >
      {children}
    </OrgContext.Provider>
  );
};

export const useOrg = () => useContext(OrgContext) || {
  memberships: [],
  activeOrgId: null,
  activeOrg: null,
  activeRole: null,
  setActiveOrgId: () => {},
  refresh: () => {},
  loading: false,
};

export const ROLE_RANK = {
  owner: 100, admin: 80, manager: 60, editor: 40, viewer: 20, client: 10,
};

export const hasRole = (role, minRole) => (ROLE_RANK[role] || 0) >= (ROLE_RANK[minRole] || 0);
